import { Table } from 'reactstrap';
import styled from 'styled-components';

import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import MapPopup from '../map/MapPopup';
import IconButton from '../basics/IconButton';
import XIcon from '../icons/XIcon';
import NewTabIcon from '../icons/NewTabIcon';
import { CropSwatchPicker } from '../right-menu/menu-components';
import { asQuantity, asTruncatedText } from '../../utils/formatters';
import { captureEvent } from '../../utils/analytics';
import { useAreaUnits } from '../../contexts/UnitContext';
import NewTabAnchor from '../basics/NewTabAnchor';

// Table with no borders and adjusted spacing.
const CropTable = styled(Table)`
  margin-bottom: 0;

  th,
  tr,
  td {
    border: none !important;
  }

  td {
    padding-top: 0.3rem;
    padding-bottom: 0.3rem;
    max-width: 300px;
  }

  td:first-child,
  th:first-child {
    padding-left: 0;
  }

  thead th {
    padding-bottom: 0;
  }
`;

const CropInfoTable = ({ cropAggregates }) => {
  const { areaFormatter, shortUnitName } = useAreaUnits();
  const { getColorForCropName } = useMultiProjectDashboardContext();

  return (
    <CropTable className="mt-2">
      <thead>
        <tr>
          <th>crop</th>
          <th>qty</th>
          <th>area</th>
        </tr>
      </thead>
      <tbody>
        {cropAggregates.map(({ crop, ecologicalSummary }) => {
          return (
            <tr key={crop.id}>
              <td className="d-flex align-items-center">
                <CropSwatchPicker
                  crop={{ color: getColorForCropName(crop.name) }}
                  colorOptions={[]}
                  color="light"
                  readonly
                />
                {asTruncatedText(crop.name)}
              </td>
              <td>{asQuantity(ecologicalSummary.treeCount)}</td>
              <td>
                {areaFormatter(ecologicalSummary.acreage)} {shortUnitName}
              </td>
            </tr>
          );
        })}
      </tbody>
    </CropTable>
  );
};

const DashboardMapInfoCard = ({ mapRef, farmData, lngLat, onClose }) => {
  const { areaFormatter, longUnitName } = useAreaUnits();
  const {
    id,
    name,
    address,
    startYear,
    cropAggregates,
    ecologicalSummary,
  } = farmData;

  return (
    <MapPopup mapRef={mapRef} lngLat={lngLat} className="p-1">
      <div>
        {/* HEADER */}
        <div className="d-flex">
          {/* TITLE */}
          <div className="d-flex pl-3 pt-3 pb-1 pr-4 min-w-0">
            <h2 className="m-0 text-truncate" title={name}>
              {name}
            </h2>
            <div className="ml-2 flex-shrink-0">
              {areaFormatter(ecologicalSummary.acreage)} {longUnitName},{' '}
              {startYear}
            </div>
          </div>

          {/* BUTTONS */}
          <div className="d-flex ml-auto pr-1 align-items-start">
            <NewTabAnchor
              className="px-1 py-2"
              href={`/app/farm/${id}/fields`}
              onClick={() =>
                captureEvent('farm-opened', {
                  location: 'multi project dashboard project card',
                  inNewTab: true,
                })
              }
            >
              <NewTabIcon width={10} height={10} className="d-block" />
            </NewTabAnchor>
            <IconButton
              color="link"
              size="sm"
              onClick={onClose}
              className="px-1 py-2 border-0"
            >
              <XIcon width={10} height={10} className="d-block" />
            </IconButton>
          </div>
        </div>

        {/* BODY */}
        <div className="px-3 pb-3">
          {address && asTruncatedText(address)}

          {/* CROPS */}
          {cropAggregates.length > 0 ? (
            <CropInfoTable cropAggregates={cropAggregates} />
          ) : (
            <div className="mt-2 text-muted">No crops</div>
          )}
        </div>
      </div>
    </MapPopup>
  );
};

export default DashboardMapInfoCard;
